"use client";

// Financing customer-video carousel — Figma 8301:28991 / 8301:73281.
// Shared across early-stage, growing, established, and customer-referral.

import { PlaySolid } from "iconoir-react";
import Image from "next/image";
import { useCallback, useRef, useState } from "react";

import { FinancingVideoProgressBar } from "@/components/blocks/financing-video-showcase/progress-bar";
import { MuxPlayer } from "@/components/post-rich-text/mux-player";
import { Dialog, DialogClose, DialogTitle } from "@/components/ui/dialog";
import { Link } from "@/components/ui/link";
import {
  VideoDialogPopup,
  videoDialogCloseCompactClass,
  videoDialogOverlayDarkClass,
} from "@/components/ui/video-dialog";
import { cn } from "@/lib/utils";

export interface FinancingVideoEpisode {
  id: string;
  title: string;
  storyLabel: string;
  href: string;
  playbackId?: string;
  thumbnailUrl?: string;
  thumbnailAlt?: string;
}

export function financingVideoThumbnail(episode: FinancingVideoEpisode) {
  if (episode.thumbnailUrl) return episode.thumbnailUrl;
  if (episode.playbackId) {
    return `https://image.mux.com/${episode.playbackId}/thumbnail.webp?fit_mode=smartcrop&time=0`;
  }
  return "";
}

interface FinancingVideoShowcaseProps {
  title: string;
  ctaLabel: string;
  ctaHref: string;
  episodes: readonly FinancingVideoEpisode[];
  className?: string;
  /** When set, wraps the section content (e.g. early-stage section shell). */
  renderShell?: (children: React.ReactNode) => React.ReactNode;
  ctaExternal?: boolean;
  dataTestId?: string;
}

export function FinancingVideoShowcase({
  title,
  ctaLabel,
  ctaHref,
  episodes,
  className,
  renderShell,
  ctaExternal = false,
  dataTestId = "financing-video-showcase",
}: FinancingVideoShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const mobileCarouselRef = useRef<HTMLDivElement>(null);

  const activeEpisode = episodes[activeIndex] ?? episodes[0];
  const openVideo = useCallback(() => setDialogOpen(true), []);

  const scrollMobileCarouselToIndex = useCallback((index: number) => {
    const container = mobileCarouselRef.current;
    if (!container) return;
    const slide = container.children[index] as HTMLElement | undefined;
    slide?.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });
  }, []);

  const handleMobileCarouselScroll = useCallback(() => {
    const container = mobileCarouselRef.current;
    if (!container) return;

    const slides = Array.from(container.children) as HTMLElement[];
    if (slides.length === 0) return;

    const scrollCenter = container.scrollLeft + container.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - scrollCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex((current) =>
      current === closestIndex ? current : closestIndex
    );
  }, []);

  const setActiveVideoIndex = useCallback(
    (index: number) => {
      setActiveIndex(index);
      scrollMobileCarouselToIndex(index);
    },
    [scrollMobileCarouselToIndex]
  );

  if (!activeEpisode) return null;

  const content = (
    <div
      className={cn(
        "mx-auto flex w-full max-w-[1280px] flex-col gap-12",
        className
      )}
      data-testid={dataTestId}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
        <h2 className="text-grey-950 text-ds-heading-h2 max-w-[524px] text-balance">
          {title}
        </h2>
        <Link
          href={ctaHref}
          external={ctaExternal}
          className="border-grey-100 bg-grey-0 text-grey-950 text-ds-label-sm inline-flex h-8 shrink-0 items-center rounded-full border px-4 font-medium"
        >
          {ctaLabel}
        </Link>
      </div>

      <div className="flex flex-col gap-12">
        <div className="hidden gap-2.5 overflow-hidden md:flex">
          {episodes.map((episode, index) => {
            const isActive = index === activeIndex;
            const poster = financingVideoThumbnail(episode);

            return (
              <div
                key={episode.id}
                className={cn(
                  "relative h-[496px] overflow-hidden rounded-lg transition-[flex,width] duration-500 ease-out",
                  isActive ? "min-w-0 flex-1" : "w-[100px] shrink-0"
                )}
              >
                {!isActive ? (
                  <button
                    type="button"
                    onMouseEnter={() => setActiveVideoIndex(index)}
                    onClick={() => setActiveVideoIndex(index)}
                    className="relative block size-full overflow-hidden rounded-lg opacity-80"
                    aria-label={`Expand ${episode.title} video`}
                    aria-expanded={false}
                  >
                    <Image
                      src={poster}
                      alt=""
                      fill
                      sizes="100px"
                      className="scale-110 object-cover blur-[11px]"
                    />
                  </button>
                ) : (
                  <div
                    className="relative size-full overflow-hidden rounded-lg"
                    onMouseEnter={() => setActiveVideoIndex(index)}
                  >
                    <div className="absolute inset-[-12px] overflow-hidden rounded-lg">
                      <Image
                        src={poster}
                        alt=""
                        fill
                        priority
                        sizes="(min-width: 768px) 840px, 100vw"
                        className="scale-105 object-cover blur-[11px]"
                      />
                    </div>
                    <Image
                      src={poster}
                      alt={episode.thumbnailAlt ?? ""}
                      fill
                      priority
                      sizes="(min-width: 768px) 840px, 100vw"
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    {episode.playbackId ? (
                      <button
                        type="button"
                        onClick={openVideo}
                        className={cn(
                          "absolute top-1/2 left-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-lg px-3 py-3",
                          "border border-white/30 bg-[linear-gradient(to_right,rgba(255,255,255,0.02),rgba(255,255,255,0.05))] text-white backdrop-blur-[3.5px]"
                        )}
                        aria-label={`Play video — ${episode.title}`}
                      >
                        <PlaySolid className="size-2.5" aria-hidden />
                        <span className="text-[14px] font-semibold tracking-[-0.03em]">
                          Play video
                        </span>
                      </button>
                    ) : null}
                    <div className="absolute bottom-10 left-10 z-10 text-left text-white">
                      <p
                        data-testid="financing-video-featured-title"
                        className="text-[24px] leading-none tracking-[-0.03em]"
                      >
                        {episode.title}
                      </p>
                      <Link
                        href={episode.href}
                        className="text-grey-0/90 mt-1.5 inline-block text-[14px] tracking-[-0.02em] hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {episode.storyLabel}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-4 overflow-x-clip md:hidden">
          <div
            ref={mobileCarouselRef}
            onScroll={handleMobileCarouselScroll}
            className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {episodes.map((episode) => {
              const poster = financingVideoThumbnail(episode);

              return (
                <button
                  key={episode.id}
                  type="button"
                  onClick={episode.playbackId ? openVideo : undefined}
                  className="relative h-[327px] w-[min(327px,calc(100vw-3rem))] shrink-0 snap-start overflow-hidden rounded-lg"
                  aria-label={`Play video — ${episode.title}`}
                >
                  <div className="absolute inset-[-12px] overflow-hidden rounded-lg">
                    <Image
                      src={poster}
                      alt=""
                      fill
                      sizes="327px"
                      className="scale-105 object-cover blur-[11px]"
                    />
                  </div>
                  <Image
                    src={poster}
                    alt={episode.thumbnailAlt ?? ""}
                    fill
                    sizes="327px"
                    className="object-cover"
                  />
                  {episode.playbackId ? (
                    <span className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-lg border border-white/30 bg-[linear-gradient(to_right,rgba(255,255,255,0.02),rgba(255,255,255,0.05))] px-3 py-3 text-white backdrop-blur-[3.5px]">
                      <PlaySolid className="size-2.5" aria-hidden />
                      <span className="text-[14px] font-semibold tracking-[-0.03em]">
                        Play video
                      </span>
                    </span>
                  ) : null}
                  <div className="absolute bottom-6 left-6 text-left text-white">
                    <p className="text-[24px] leading-none tracking-[-0.03em]">
                      {episode.title}
                    </p>
                    <Link
                      href={episode.href}
                      className="mt-1.5 inline-block text-[14px] tracking-[-0.02em] text-white/90"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {episode.storyLabel}
                    </Link>
                  </div>
                </button>
              );
            })}
          </div>

          <FinancingVideoProgressBar
            activeIndex={activeIndex}
            episodeCount={episodes.length}
          />
        </div>

        <div className="hidden md:flex">
          <FinancingVideoProgressBar
            activeIndex={activeIndex}
            episodeCount={episodes.length}
            className="w-full"
          />
        </div>
      </div>

      {activeEpisode.playbackId ? (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <VideoDialogPopup
            variant="card-compact"
            overlayClassName={videoDialogOverlayDarkClass}
          >
            <DialogTitle className="sr-only">{activeEpisode.title}</DialogTitle>
            <MuxPlayer
              playbackId={activeEpisode.playbackId}
              className="aspect-video w-full"
            />
            <DialogClose className={videoDialogCloseCompactClass}>
              Close
            </DialogClose>
          </VideoDialogPopup>
        </Dialog>
      ) : null}
    </div>
  );

  if (renderShell) {
    return <>{renderShell(content)}</>;
  }

  return content;
}
